import React from 'react';
import {
    FaEye,
    FaCheck,
    FaTimes,
    FaFileAlt,
    FaDownload,
    FaCheckCircle,
    FaClock,
    FaPause,
    FaExclamationTriangle
} from 'react-icons/fa';

const CustomerTable = ({
    customers,
    onViewCustomer,
    onUpdateStatus,
    onViewDocument,
    onDownloadDocument
}) => {

    // B2B provision status badge — separate from customer.status (which is admin-level).
    // provisionStatus = null | 'pending' | 'active' | 'inactive' | 'rejected' | 'withdrawn' | 'not_provisioned'
    const getProvisionBadge = (provisionStatus, snapshot, portalProvisioned) => {
        // portalProvisioned + provisionStatus='active' just shows plain 'active' — these
        // customers are functionally the same as API-provisioned active ones. The flag
        // still gates API re-provision attempts at the Lambda layer (handler refuses).
        if (!provisionStatus || provisionStatus === 'not_provisioned') {
            return { label: 'not provisioned', color: 'text-[#928374] bg-[#3c3836] border border-[#504945]', animated: false };
        }
        // pending shows the live TCAS sub-state when available (e.g. "pending · PROCESSING")
        const sub = snapshot?.lastStatus && ['QUEUED','PROCESSING'].includes(snapshot.lastStatus) ? snapshot.lastStatus : null;
        switch (provisionStatus) {
            case 'active':    return { label: 'active', color: 'text-[#b8bb26] bg-[#3c3836] border border-[#458588]', animated: false };
            case 'pending':   return { label: sub ? `pending · ${sub.toLowerCase()}` : 'pending', color: 'text-[#fabd2f] bg-[#3c3836] border border-[#d79921]', animated: true };
            case 'inactive':  return { label: 'inactive', color: 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]', animated: false };
            case 'rejected':  return { label: 'rejected', color: 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]', animated: false };
            case 'withdrawn': return { label: 'withdrawn', color: 'text-[#d3869b] bg-[#3c3836] border border-[#b16286]', animated: false };
            default:          return { label: provisionStatus, color: 'text-[#bdae93] bg-[#3c3836] border border-[#504945]', animated: false };
        }
    };

    // Acquisition channel. Recorded on the order since 2026-08-25; customers who
    // signed up before that have no value and must read as "not recorded" rather
    // than being quietly bucketed into web.
    const getSourceBadge = (source) => {
        switch (source) {
            case 'ios':
                return { label: 'iOS app', color: 'text-[#83a598] bg-[#3c3836] border border-[#458588]', title: 'Signed up in the iOS app' };
            case 'android':
                return { label: 'Android app', color: 'text-[#b8bb26] bg-[#3c3836] border border-[#98971a]', title: 'Signed up in the Android app' };
            case 'web':
                return { label: 'Web', color: 'text-[#d3869b] bg-[#3c3836] border border-[#b16286]', title: 'Signed up in a browser' };
            default:
                return {
                    label: '—',
                    color: 'text-[#7c6f64] bg-[#3c3836] border border-[#504945]',
                    title: 'Not recorded — this customer signed up before the source was captured',
                };
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'text-[#b8bb26] bg-[#3c3836] border border-[#458588]';
            case 'pending': return 'text-[#fabd2f] bg-[#3c3836] border border-[#d79921]';
            case 'on_hold': return 'text-[#fe8019] bg-[#3c3836] border border-[#d79921]';
            case 'cancelled': return 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]';
            case 'rejected': return 'text-[#fb4934] bg-[#3c3836] border border-[#cc241d]';
            default: return 'text-[#bdae93] bg-[#3c3836] border border-[#504945]';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <FaCheckCircle className="text-[#b8bb26]" />;
            case 'pending': return <FaClock className="text-[#fabd2f]" />;
            case 'on_hold': return <FaPause className="text-[#fe8019]" />;
            case 'rejected': return <FaTimes className="text-[#fb4934]" />;
            case 'cancelled': return <FaTimes className="text-[#fb4934]" />;
            default: return <FaExclamationTriangle className="text-[#bdae93]" />;
        }
    };

    return (
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#3c3836]">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                                Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Plan
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Document Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Provision
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Source
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Created
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-[#928374] uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-[#282828] divide-y divide-[#3c3836]">
                        {customers.map((customer, index) => (
                            <tr
                                key={customer.UID || customer.accountNumber || customer.email || `customer-${index}`}
                                className="hover:bg-[#3c3836]"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-gradient-to-r from-[#fabd2f] to-[#fe8019] rounded-full flex items-center justify-center text-[#1d2021] font-bold">
                                            {(customer.firstName || customer.fullName || 'U').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-[#ebdbb2]">{customer.fullName}</div>
                                            <div className="text-sm text-[#bdae93]">{customer.email}</div>
                                            <div className="text-xs text-[#7c6f64]">{customer.accountNumber}</div>
                                            {customer.avcId && <div className="text-xs text-[#fabd2f] font-mono mt-0.5">AVC: {customer.avcId}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-[#ebdbb2]">{customer.plan}</div>
                                    <div className="text-sm text-[#bdae93]">${customer.monthlyFee}/month</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                                        {getStatusIcon(customer.status)}
                                        <span className="ml-1">{customer.status.replace('_', ' ')}</span>
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                        {customer.uploadedDocument ? (
                                            <div className="flex items-center space-x-2">
                                                <span className="px-2 py-1 bg-[#3c3836] text-[#b8bb26] text-xs rounded-full">
                                                    Uploaded
                                                </span>
                                                <button
                                                    onClick={() => onViewDocument(customer)}
                                                    className="flex items-center space-x-1 text-[#fabd2f] hover:text-[#fe8019]"
                                                    title="View Document"
                                                >
                                                    <FaFileAlt size={14} />
                                                    <span className="text-sm">View</span>
                                                </button>
                                                <button
                                                    onClick={() => onDownloadDocument(customer)}
                                                    className="flex items-center space-x-1 text-[#b8bb26] hover:text-[#d5e39f]"
                                                    title="Download Document"
                                                >
                                                    <FaDownload size={14} />
                                                    <span className="text-sm">Download</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center space-x-2">
                                                <span className="px-2 py-1 bg-[#3c3836] text-[#fb4934] text-xs rounded-full">
                                                    Pending
                                                </span>
                                                <span className="text-sm text-[#bdae93]">No document</span>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {(() => {
                                        const badge = getProvisionBadge(customer.provisionStatus, customer.vocusOrderSnapshot, customer.portalProvisioned);
                                        const tx = customer.vocusOrderSnapshot?.transactionId;
                                        const portalAvc = customer.portalProvisionedAvcId;
                                        return (
                                            <div className="flex flex-col">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color} ${badge.animated ? 'animate-pulse' : ''} w-fit`}>
                                                    {badge.label}
                                                </span>
                                                {tx && <span className="text-xs text-[#7c6f64] font-mono mt-0.5" title="Click the server icon to view full status">TX: {tx.slice(0, 12)}…</span>}
                                                {!tx && portalAvc && <span className="text-xs text-[#7c6f64] font-mono mt-0.5" title="Provisioned via Vocus portal (out-of-band)">AVCID: {portalAvc.slice(0, 12)}…</span>}
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {(() => {
                                        const s = getSourceBadge(customer.signupSource);
                                        return (
                                            <span
                                                className={`px-2 py-1 text-xs rounded-full ${s.color}`}
                                                title={s.title}
                                            >
                                                {s.label}
                                                {/* Backfilled values were inferred from the User-Agent,
                                                    not declared by the client. Marked so nobody reads
                                                    them as first-party data. */}
                                                {customer.signupSourceInferred ? ' ~' : ''}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#bdae93]">
                                    {new Date(customer.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex items-center space-x-2">
                                        {/* View Details — the only entry point for approve/reject/etc.
                                            Row-level quick-approve/quick-reject were removed: they bypassed
                                            the ApprovalModal which captures notes + serviceActivationDate
                                            + AVC ID, and a one-click reject without notes was unsafe. */}
                                        <button
                                            onClick={() => onViewCustomer(customer)}
                                            className="text-[#fabd2f] hover:text-[#fe8019] p-1"
                                            title="View Details (approve / reject / provision live here)"
                                        >
                                            <FaEye size={16} />
                                        </button>
                                        {/* Per-customer Provision action removed — provisioning now lives in the
                                            standalone "Provision Service" admin tab. The status badge above is kept
                                            for visibility; the markProvisioned recorder stays in CustomerDetailsModal. */}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomerTable;
